/** Vendas por canal × meta em níveis (13/10/2026): loja → categoria → SKU → anúncio → pedidos do anúncio.
 * Números prontos em `faturamentoNiveis` (logic.ts); os pedidos vêm da mesma rota do detalhe do SKU, só ao abrir. */
import { Fragment, useEffect, useState, type ReactNode } from 'react'
import { Alert, Box, Link, Table, TableBody, TableCell, TableHead, TableRow, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import { TabelaPedidos } from './DetalheSku'
import { carregarPedidosAnuncio } from './fonte'
import { Mc, Seta, StatusChip } from './componentes'
import { brl, num, pct, type NoFaturamento } from './logic'
import type { PedidosAnuncio } from './types'

const COLUNAS = ['Loja / categoria / SKU / anúncio', 'Status', 'Fat. 7d', 'Meta 7d', 'Ritmo', 'Temporada × meta acum.', 'Participação real × meta', 'Peças 7d', 'MC']
const RECUO = { 1: 0, 2: 2.5, 3: 5, 4: 7.5 } as const

export function FaturamentoNiveis({ linhas }: { linhas: NoFaturamento[] }) {
  const [abertos, setAbertos] = useState<Set<string>>(new Set())
  const alternar = (k: string) => setAbertos((s) => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n })
  if (!linhas.length) return <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>Sem lojas no filtro.</Typography>

  const linha = (x: NoFaturamento): ReactNode => {
    const abre = x.nivel < 4 ? x.filhos.length > 0 : !!x.pedidos
    const aberto = abertos.has(x.chave)
    return (
      <Fragment key={x.chave}>
        <TableRow hover onClick={abre ? () => alternar(x.chave) : undefined}
          sx={{ cursor: abre ? 'pointer' : undefined, ...(x.nivel === 1 ? { '& td': { fontWeight: 700 } } : {}), ...(x.nivel === 4 ? { bgcolor: 'action.hover' } : {}) }}>
          <TableCell sx={{ pl: 1 + RECUO[x.nivel], maxWidth: 420 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
              {abre ? <Seta aberta={aberto} /> : <Box component="span" sx={{ width: 16, flexShrink: 0 }} />}
              <Box sx={{ minWidth: 0 }}>
                <Box sx={{ whiteSpace: 'normal', fontWeight: x.nivel === 3 ? 600 : undefined }}>
                  {x.link ? <Link href={x.link} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>{x.rotulo}</Link> : x.rotulo}
                </Box>
                {x.sub && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', whiteSpace: 'normal' }}>{x.sub}</Typography>}
              </Box>
            </Box>
          </TableCell>
          <TableCell>{x.status ? <StatusChip s={x.status} /> : null}</TableCell>
          <TableCell align="right">{brl(x.fat7)}<Pendente v={x.pend7} /></TableCell>
          <TableCell align="right">{x.meta7 == null ? '—' : brl(x.meta7)}</TableCell>
          <TableCell align="right">{x.ritmo == null ? '—' : x.ritmo.toFixed(2).replace('.', ',')}</TableCell>
          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{x.metaAcum == null ? brl(x.temporada) : `${brl(x.temporada)} × ${brl(x.metaAcum)}`}<Pendente v={x.pendTemp} /></TableCell>
          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{x.partMeta == null ? pct(x.partReal) : `${pct(x.partReal)} × ${pct(x.partMeta)}`}</TableCell>
          <TableCell align="right">{num(x.pecas7)}</TableCell>
          <TableCell align="right"><Mc v={x.mc} /></TableCell>
        </TableRow>
        {aberto && x.nivel < 4 && x.filhos.map(linha)}
        {aberto && x.nivel === 4 && x.pedidos && (
          <TableRow><TableCell colSpan={COLUNAS.length} sx={{ pl: 1 + RECUO[4] + 2, bgcolor: 'action.hover' }}><Pedidos {...x.pedidos} /></TableCell></TableRow>
        )}
        {aberto && x.nivel === 3 && x.filhos.length === 0 && (
          <TableRow><TableCell colSpan={COLUNAS.length} sx={{ pl: 1 + RECUO[4] }}><Typography variant="caption" color="text.secondary">Sem anúncio ativo nem venda deste SKU nesta loja.</Typography></TableCell></TableRow>
        )}
      </Fragment>
    )
  }

  return (
    <Box sx={{ overflow: 'auto', maxHeight: '75vh', mt: 1 }}>
      <Table size="small" stickyHeader>
        <TableHead><TableRow>{COLUNAS.map((c, i) => <TableCell key={c} align={i < 2 ? 'left' : 'right'} sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{c}</TableCell>)}</TableRow></TableHead>
        <TableBody>{linhas.map(linha)}</TableBody>
      </Table>
    </Box>
  )
}

/** Parte do valor que vem de pedidos pagos ainda sem margem oficial (envio pendente ou retido pelo marketplace). */
function Pendente({ v }: { v: number }) {
  if (v < 0.5) return null
  return (
    <Typography variant="caption" color="warning.main" sx={{ display: 'block', whiteSpace: 'nowrap' }}
      title="Pedidos pagos que ainda não têm margem oficial (o envio está pendente ou retido pelo marketplace). Entram com o valor pago e MC estimada; viram oficiais sozinhos quando a margem chega.">
      inclui {brl(v)} margem pendente
    </Typography>
  )
}

/** Nível 5: pedidos da margem oficial do anúncio, só os do SKU da linha (e os pagos ainda sem margem, marcados).
 * `recorte` (Vendas por anúncio): datas e variação fixas, sem a escolha 7 dias/temporada. */
export function Pedidos({ loja, anuncio, sku, skus, recorte }: { loja: string; anuncio: string; sku?: string; skus?: string[]
  recorte?: { de: string; ate: string; variacao: string | null } }) {
  const lista = skus ?? (sku ? [sku] : [])
  const chaveSkus = lista.join(',')
  const de = recorte?.de, ate = recorte?.ate, variacao = recorte?.variacao ?? null
  const [periodo, setPeriodo] = useState<'7d' | 'temp'>('7d')
  const [res, setRes] = useState<PedidosAnuncio | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [carregando, setCarregando] = useState(false)
  useEffect(() => {
    let vivo = true
    setCarregando(true); setErro(null)
    carregarPedidosAnuncio(loja, anuncio, periodo, chaveSkus ? chaveSkus.split(',') : [], de ? { de, ate, variacao } : {})
      .then((r) => { if (vivo) setRes(r) })
      .catch((e: unknown) => { if (vivo) { setRes(null); setErro(e instanceof Error ? e.message : String(e)) } })
      .finally(() => { if (vivo) setCarregando(false) })
    return () => { vivo = false }
  }, [loja, anuncio, chaveSkus, periodo, de, ate, variacao])
  return (
    <Box sx={{ py: 0.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 1 }}>
        <Typography variant="subtitle2">Pedidos de {lista.join(', ')} neste anúncio{recorte ? ` · ${recorte.de.slice(8, 10)}/${recorte.de.slice(5, 7)} a ${recorte.ate.slice(8, 10)}/${recorte.ate.slice(5, 7)}` : ''}</Typography>
        {!recorte && (
          <ToggleButtonGroup size="small" exclusive value={periodo} onChange={(_, v: '7d' | 'temp' | null) => v && setPeriodo(v)}>
            <ToggleButton value="7d">7 dias</ToggleButton>
            <ToggleButton value="temp">Temporada</ToggleButton>
          </ToggleButtonGroup>
        )}
        {carregando && <Typography variant="caption" color="text.secondary">carregando…</Typography>}
      </Box>
      {erro && <Alert severity="warning">{erro}</Alert>}
      {!erro && !carregando && res && res.linhas.length === 0 && <Typography variant="body2">Nenhum pedido {lista.length > 1 ? 'destes SKUs' : 'deste SKU'} neste anúncio no período.</Typography>}
      {!erro && res && res.linhas.length > 0 && <TabelaPedidos res={res} carregando={carregando} alturaMax="40vh" />}
    </Box>
  )
}
